import BaseAdapter from "@/adapters/base";
import store from "@/store";
import { AxiosRequestConfig, AxiosError, AxiosResponse } from 'axios';
import User from "@/models/User";
import UsageReport from "@/models/UsageReport";
import Address from "@/models/Address";
import BillingEvent from "@/models/BillingEvent";
import Invoice from "@/models/Invoice";


export default class AdminAdapter extends BaseAdapter {

    request(options: AxiosRequestConfig): any {
        //Overwrite auth if logged in
        if (store.getters.isLoggedIn) {
            options.auth = {
                username: store.state.account.username,
                password: store.state.account.masterHash,
            };
        }
        return super.request(options);
    }

    public getUsersList(): Promise<User[]> {
        return new Promise(((resolve, reject) => {
            const url = `/users`;
            this.request({
                method: 'get',
                url,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public getInvitationToken(u: User): Promise<string> {
        return new Promise(((resolve, reject) => {
            const url = `/users/` + u.id + "/invitationToken";
            this.request({
                method: 'get',
                url,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public createUser(u: User): Promise<User> {
        return new Promise(((resolve, reject) => {
            const url = `/users/invite`;
            this.request({
                method: 'post',
                url,
                data: u,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public getUsageReportForUser(u: User): Promise<UsageReport> {
        return new Promise(((resolve, reject) => {
            const url = `/users/` + u.id + "/usage";
            this.request({
                method: 'get',
                url,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public deleteUser(u: User): Promise<User> {
        return new Promise(((resolve, reject) => {
            const url = `/users/` + u.id;
            this.request({
                method: 'delete',
                url,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public redeemInviation(u: User, invitationToken: string): Promise<void> {
        return new Promise(((resolve, reject) => {
            const url = `/users/redeem/` + encodeURIComponent(invitationToken);
            this.request({
                method: 'post',
                url,
                data: u,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public checkInviation(username: string, invitationToken: string): Promise<void> {
        return new Promise(((resolve, reject) => {
            const url = `/users/` + encodeURIComponent(username) + `/redeem/` + encodeURIComponent(invitationToken);
            this.request({
                method: 'get',
                url,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public initiatePasswordReset(email: string): Promise<void> {
        return new Promise(((resolve, reject) => {
            const url = `/account/resetpassword/${email}`;
            this.request({
                method: 'get',
                url,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public resetPassword(nonce: string, data: User): Promise<void> {
        return new Promise(((resolve, reject) => {
            const url = `/account/resetpassword/${nonce}`;
            this.request({
                method: 'post',
                url,
                data,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public addCreditAndSetAutoRenew(data: any): Promise<void> {
        return new Promise(((resolve, reject) => {
            const url = `/account/addCreditAndSetAutoRenew`;
            this.request({
                method: 'post',
                url,
                data,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public initiatePaymentDetailsChange(): Promise<any> {
        return new Promise(((resolve, reject) => {
            const url = `/account/paymentDetails`;
            this.request({
                method: 'post',
                url,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public getSubscriptionDetails(): Promise<any> {
        return new Promise(((resolve, reject) => {
            const url = `/account/subscriptionDetails`;
            this.request({
                method: 'get',
                url,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public getBillingAddress(): Promise<Address> {
        return new Promise(((resolve, reject) => {
            const url = `/account/billingAddress`;
            this.request({
                method: 'get',
                url,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public changeBillingAddress(data: Address): Promise<Address> {
        return new Promise(((resolve, reject) => {
            const url = `/account/billingAddress`;
            this.request({
                method: 'post',
                url,
                data,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public getInvoices(): Promise<Invoice[]> {
        return new Promise(((resolve, reject) => {
            const url = `/account/invoices`;
            this.request({
                method: 'get',
                url,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }

    public getBillingEvents(): Promise<BillingEvent[]> {
        return new Promise(((resolve, reject) => {
            const url = `/account/billingEvents`;
            this.request({
                method: 'get',
                url,
            }).then((res: AxiosResponse) => {
                resolve(res.data);
            }).catch((error: AxiosError) => {
                reject(error);
            });
        }));
    }
}
